import { FC, Fragment } from 'react';

import {
  Content,
  Footer,
  Form,
  Loading,
  Typography,
} from '@wearehelpful/ui-kit';
import { Redirect, useRoute } from '@core/navigation';
import { useDate, useTranslation } from '@wearehelpful/ui-localization';
import {
  BDTO,
  useCreateSurvey,
  useCreateTherapistSchedule,
  useGetTherapistScheduleDates,
} from '@helpful/network';
import { useContextSelector } from 'use-context-selector';
import { AnalyticsEvent, RoutePath } from '@common/constants';
import { useAnalytics } from '@wearehelpful/ui-stats';

import { MatchingContext } from '../Matching.context';

import { ScheduleDate } from './_partitions/ScheduleDate';
import { ScheduleSlot } from './_partitions/ScheduleSlot';
import { ScheduleParams, ScheduleSearch, ScheduleForm } from './Schedule.types';

export const Schedule: FC = () => {
  const [{ params, search }] = useRoute<ScheduleParams, ScheduleSearch>();
  const { t } = useTranslation();
  const { now } = useDate();
  const { track } = useAnalytics();

  const survey = useContextSelector(MatchingContext, ({ survey }) => survey);

  const { isLoading, data } = useGetTherapistScheduleDates(params.therapistId, {
    event: BDTO.TherapistEventType.consult,
    next: {
      unit: 'month',
      amount: 1,
    },
  });

  const {
    data: event,
    isSuccess: isScheduled,
    isLoading: isScheduleLoading,
    mutate: scheduleEvent,
  } = useCreateTherapistSchedule();

  const {
    isSuccess: isCreated,
    isLoading: isCreateLoading,
    mutate: createSurvey,
  } = useCreateSurvey();

  const handleOnSubmit = ({ dateTime }: ScheduleForm) => {
    scheduleEvent(
      {
        id: params.therapistId,
        data: {
          dateTime,
          event: BDTO.TherapistEventType.consult,
          location: {
            type: BDTO.CreateTherapistScheduleLocationType.PhoneCall,
            phoneNumber: survey?.response?.phoneNumber,
          },
          attendees: [
            {
              firstName: survey?.response?.firstName,
              lastName: survey?.response?.lastName,
              email: survey?.response?.email,
            },
          ],
        },
      },
      {
        onSuccess: () => {
          track({
            id: AnalyticsEvent.CONSULT_SELF_SCHEDULED,
            properties: {
              user: {
                firstName: survey?.response?.firstName,
                lastName: survey?.response?.lastName,
                email: survey?.response?.email,
                phoneNumber: survey?.response?.phoneNumber,
              },
              event: {
                dateTime,
                insuranceProvider: survey?.response?.insuranceProvider,
              },
            },
          });
        },
      }
    );
    createSurvey({
      data: {
        slug: 'therapists-matching',
        isCompleted: true,
        parentId: search.parentId,
        completedAt: now({ format: 'iso' }) as string,
        response: {
          dateTime,
          ...params,
          attendees: [
            {
              firstName: survey?.response?.firstName,
              lastName: survey?.response?.lastName,
              email: survey?.response?.email,
            },
          ],
        },
      },
    });
  };

  if (isScheduled && isCreated) {
    return (
      <Redirect
        path={RoutePath.THERAPISTS_MATCHING_SUCCESS}
        params={{ ...params, eventId: event.id }}
        search={{ ...search }}
      />
    );
  }

  return (
    <Fragment>
      <Loading loading={isLoading} />
      <Form<ScheduleForm>
        onSubmit={handleOnSubmit}
        key={String(isLoading)}
        defaultValues={{ date: data?.first }}
      >
        <Content>
          <Content.Subheader>
            <Typography variant="h2">
              {t('subheadings.therapistsMatchingSchedule.title')}
            </Typography>
          </Content.Subheader>
          <Content.Progress value={2 / 3} />
          <Content.Fixed>
            <Form.Section
              description={t('forms.therapistMatchingScheduleInformation')}
            >
              <ScheduleDate max={data?.to} days={data?.days} />
              <ScheduleSlot id={params?.therapistId} />
            </Form.Section>
          </Content.Fixed>
        </Content>
        <Footer shadow>
          <Form.Submit
            loading={isScheduleLoading || isCreateLoading}
            checkValidity
          >
            {t('actions.next')}
          </Form.Submit>
        </Footer>
      </Form>
    </Fragment>
  );
};
